import { useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import {
  Pencil,
  Trash,
  Download,
  Upload,
  Calculator,
  Radical,
} from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { FuelType } from "@/types";
import { ConsumptionFactorForm } from "./ConsumptionFactorForm";
import { toast } from "react-toastify";
import { PageHeader } from "@/components/PageHeader";

interface VehicleType {
  id: number;
  name: string;
}

interface ConsumptionFactor {
  id: number;
  year: number;
  fuel_type: FuelType;
  vehicle_type: VehicleType;
  coefficient: number;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const ConsumptionFactorList = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("none");
  const [selectedFuelType, setSelectedFuelType] = useState<string>("none");
  const [selectedVehicleType, setSelectedVehicleType] =
    useState<string>("none");
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const PAGE_SIZE = 10;

  // Fetch consumption factors
  const { data, isLoading, error } = useQuery<ApiResponse<ConsumptionFactor>>({
    queryKey: [
      "consumption-factors",
      currentPage,
      selectedYear,
      selectedFuelType,
      selectedVehicleType,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `consumption-factors/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (selectedYear !== "none") {
        url += `&year=${selectedYear}`;
      }

      if (selectedFuelType !== "none") {
        url += `&fuel_type=${selectedFuelType}`;
      }

      if (selectedVehicleType !== "none") {
        url += `&vehicle_type=${selectedVehicleType}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(t("coefficients.consumption.error.fetch"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  // Fetch fuel types
  const { data: fuelTypes } = useQuery<ApiResponse<FuelType>>({
    queryKey: ["fuel-types"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl("fuel-types/?limit=9999&category=vehicle"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error(t("coefficients.consumption.error.fetchFuelTypes"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  // Fetch vehicle types
  const { data: vehicleTypes } = useQuery<ApiResponse<VehicleType>>({
    queryKey: ["vehicle-types"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("vehicle-types/"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(t("coefficients.consumption.error.fetchVehicleTypes"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`consumption-factors/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete consumption factor");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["consumption-factors"] });
      toast.success(t("common.success.deleted"));
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setItemToDelete(null);
    },
  });

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleFuelTypeChange = (fuelType: string) => {
    setSelectedFuelType(fuelType);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleVehicleTypeChange = (vehicleType: string) => {
    setSelectedVehicleType(vehicleType);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSuccess = () => {
    setIsDrawerOpen(false);
    queryClient.invalidateQueries({ queryKey: ["consumption-factors"] });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file extension
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error(t("common.invalidFileFormat"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsImporting(true);

    try {
      const response = await fetch(
        getApiUrl("consumption-factors/import_data/"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t("common.importError"));
      }

      // Show success message with details
      toast.success(
        t("common.importSuccess", {
          created: result.created,
          skipped: result.skipped,
          errors: result.errors || 0,
        }),
      );

      // Refresh the data
      queryClient.invalidateQueries({ queryKey: ["consumption-factors"] });
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(
        error instanceof Error ? error.message : t("common.importError"),
      );
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExport = async () => {
    try {
      // Build query parameters based on current filters
      const params = new URLSearchParams();

      if (selectedYear !== "none") {
        params.append("year", selectedYear);
      }

      if (selectedFuelType !== "none") {
        params.append("fuel_type", selectedFuelType);
      }

      if (selectedVehicleType !== "none") {
        params.append("vehicle_type", selectedVehicleType);
      }

      // Add pagination parameters
      params.append("limit", "99999"); // Export all records

      // Create the export URL
      const exportUrl = `${getApiUrl(
        `consumption-factors/export/`,
      )}?${params.toString()}`;

      // Make the API request with proper headers
      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the response as a blob
      const blob = await response.blob();

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);

      // Create a temporary URL for the blob
      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("content-disposition");
      let filename = "consumption_factors_export.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch != null && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }
      a.download = filename;
      document.body.appendChild(a);

      // Trigger the download
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error("Expoºrt failed:", error);
      toast.error(t("common.exportError"));
    }
  };

  if (error) {
    return (
      <div className="text-destructive">
        {t("common.error")}: {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={<Radical className="w-12 h-12 text-primary-foreground" />}
        actionTitle="coefficients.consumption.add"
        title="coefficients.consumption.title"
        description="coefficients.consumption.description"
        handleNew={handleAdd}
      >
        <Button
          variant="outline"
          onClick={handleExport}
          className="flex items-center"
        >
          <Download className="h-4 w-4 mr-2" />
          {t("common.export")}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          className="hidden"
          disabled={isImporting}
        />
        <Button
          variant="outline"
          onClick={handleImportClick}
          disabled={isImporting}
        >
          <Upload className="mr-2 h-4 w-4" />
          {isImporting ? t("common.importing") : t("common.import")}
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="flex items-center space-x-2 p-4">
        <div className="flex flex-col space-y-2">
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("filters.selectYear")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("filters.allYears")}</SelectItem>
              {Array.from({ length: 11 }, (_, i) => {
                const year = new Date().getFullYear() - 5 + i;
                return (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col space-y-2">
          {/* <label className="text-sm font-medium">
            {t("coefficients.consumption.filters.fuelType")}
          </label> */}
          <Select value={selectedFuelType} onValueChange={handleFuelTypeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("filters.selectFuelType")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("filters.allFuelTypes")}</SelectItem>
              {fuelTypes?.results?.map((fuelType) => (
                <SelectItem key={fuelType.id} value={fuelType.id.toString()}>
                  {fuelType.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col space-y-2">
          {/* <label className="text-sm font-medium">
            {t("coefficients.consumption.filters.vehicleType")}
          </label> */}
          <Select
            value={selectedVehicleType}
            onValueChange={handleVehicleTypeChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("filters.selectVehicleType")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t("filters.allVehicleTypes")}
              </SelectItem>
              {vehicleTypes?.results?.map((vehicleType) => (
                <SelectItem
                  key={vehicleType.id}
                  value={vehicleType.id.toString()}
                >
                  {vehicleType.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("coefficients.consumption.fields.year")}
                </TableHead>
                <TableHead>
                  {t("coefficients.consumption.fields.fuelType")}
                </TableHead>
                <TableHead>
                  {t("coefficients.consumption.fields.vehicleType")}
                </TableHead>
                <TableHead>
                  {t("coefficients.consumption.fields.coefficient")}
                </TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-[80px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[150px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[150px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end space-x-2">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : data?.results?.length ? (
                data.results.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell variant="compact" className="font-medium">
                      {item.year}
                    </TableCell>
                    <TableCell variant="compact">
                      {item.fuel_type?.name || "-"}
                    </TableCell>
                    <TableCell variant="compact">
                      {item.vehicle_type?.name || "-"}
                    </TableCell>
                    <TableCell variant="compact" className="text-center">
                      {item.coefficient}
                    </TableCell>
                    <TableCell variant="compact" className="text-right">
                      <div className="flex justify-center items-center">
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => handleEdit(item.id)}
                        >
                          <Pencil
                            size={30}
                            fill="#000000"
                            stroke="#ffffff"
                            strokeWidth={1}
                          />
                        </div>
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => setItemToDelete(item.id)}
                        >
                          <Trash
                            size={30}
                            fill="#ff0000"
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    {t("common.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil((data?.count || 0) / PAGE_SIZE)}
            hasNextPage={!!data?.next}
            hasPreviousPage={!!data?.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <DeleteModal
        open={itemToDelete !== null}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        entity={t("coefficients.consumption.entityName")}
        onDelete={() => deleteMutation.mutate(itemToDelete!)}
        isPending={deleteMutation.isPending}
      />

      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsDrawerOpen(false);
            setEditingId(null);
          }
        }}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingId
                ? t("coefficients.consumption.editTitle")
                : t("coefficients.consumption.addTitle")}
            </DrawerTitle>
            <DrawerDescription>
              {editingId
                ? t("coefficients.consumption.editDescription")
                : t("coefficients.consumption.addDescription")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-6">
            <ConsumptionFactorForm
              id={editingId || undefined}
              onSuccess={handleSuccess}
              onCancel={() => {
                setIsDrawerOpen(false);
                setEditingId(null);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
